import { FC, ReactNode, useEffect, useState } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Chip, Group, Stack, Text, Tooltip } from "@mantine/core";

import { slugify } from "@shared/utils";

import styles from "./FilterChips.module.css";

interface Data {
  label: string;
  value: string;
  count?: number;
  description?: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
}

export type FilterChipsProps =
  | {
      multiple: true;
      data: Data[];
      value: string[] | undefined;
      onChange: (value: string[]) => void;
      label?: string;
      tooltip?: ReactNode;
      tooltipIcon?: IconProp;
      disabled?: boolean;
      disabledMessage?: string;
    }
  | {
      multiple?: false;
      data: Data[];
      value: string | undefined;
      onChange: (value: string) => void;
      label?: string;
      tooltip?: ReactNode;
      tooltipIcon?: IconProp;
      disabled?: boolean;
      disabledMessage?: string;
    };

export const FilterChips: FC<FilterChipsProps> = ({
  data,
  multiple = false,
  value,
  onChange,
  label,
  tooltip,
  tooltipIcon = faQuestionCircle,
  disabled,
  disabledMessage,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  useEffect(() => {
    setSelectedItems(
      data
        .filter((item) =>
          multiple ? value?.includes(item.value) : value === item.value
        )
        .map((item) => item.value)
    );
  }, [value, data, multiple]);
  return (
    <Stack id={label && slugify("filterchips-" + label)} gap={0}>
      {label && (
        <Group mb={2} gap="xs">
          <Text component="label" size="md" fw={500} c="dark.4" htmlFor={label}>
            {label}
          </Text>
          {tooltip && (
            <Tooltip
              label={tooltip}
              withinPortal
              w={300}
              multiline
              position="right-start"
            >
              <Text fz="md" fw={500} c="gray.6">
                <FontAwesomeIcon icon={tooltipIcon} />
              </Text>
            </Tooltip>
          )}
        </Group>
      )}
      <Chip.Group
        key={label}
        value={value}
        multiple={multiple}
        onChange={onChange as (value: string | string[]) => void}
      >
        <Group gap={4} fz="1em" fw={500}>
          {data.map((item) => (
            <Tooltip
              key={item.value}
              label={disabledMessage || "This filter is disabled."}
              disabled={!disabled}
              withinPortal
            >
              <div>
                <Chip
                  key={item.value}
                  classNames={{
                    input: styles.input,
                    label: styles.label,
                  }}
                  value={item.value}
                  data-testid={`filter-chip-${item.value}`}
                  mt={4}
                  styles={{
                    label: {
                      padding: "4px 12px",
                      // we set all background colors manually because white cannot be forced on off-state otherwise
                      backgroundColor: selectedItems.includes(item.value)
                        ? "var(--mantine-color-blue-7)"
                        : "white",
                    },
                  }}
                  disabled={disabled}
                >
                  <Group justify="space-between" gap="sm">
                    <Group gap={4}>
                      {item.leftSection && item.leftSection}
                      {item.label}
                    </Group>

                    {Boolean(item.count) && (
                      <Text
                        component="span"
                        size="xs"
                        className="count"
                        fw={500}
                        lh="initial"
                      >
                        {item.count}
                      </Text>
                    )}

                    {item.rightSection && item.rightSection}
                  </Group>
                </Chip>
              </div>
            </Tooltip>
          ))}
        </Group>
      </Chip.Group>
    </Stack>
  );
};
